import { DASHBOARD_ACTIONS, type DashboardActionsType } from './DashboardDuck.actions'
import type { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import type { DashboardDuckStateSelectorsType } from './DashboardDuck.types'
import SagaDuck from '@duckness/saga'
import DashboardSchemaModel, {
  getDashboardSchemaModelFrom,
  getEmptyDashboardSchemaModel
} from '@aytm/app/insights/dashboard/models/DashboardSchemaModel'
import { duckName } from './constants'
import BlockModel, { BlocksOrderedMap } from '@aytm/app/insights/dashboard/models/BlockModel'
import { getDefaultTimezoneOffset } from '@aytm/app/insights/dashboard/lib/datetime'
import { updateCurrentData } from '@aytm/app/insights/dashboard/helpers/requests'
import { SendStatus, type SendStatusType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'

export const DashboardDuck = SagaDuck<
  InsightsDashboardPoolState,
  DashboardActionsType,
  DashboardDuckStateSelectorsType
>(duckName, 'insights-dashboard')

// actions
DashboardDuck.action(DASHBOARD_ACTIONS.UPDATE_TITLE)
DashboardDuck.action(DASHBOARD_ACTIONS.RECEIVE_TITLE)
DashboardDuck.action(DASHBOARD_ACTIONS.CLONE_PORTAL)
DashboardDuck.action(DASHBOARD_ACTIONS.CLONE_SET)
DashboardDuck.action(DASHBOARD_ACTIONS.REQUEST_SHARE_UI)
DashboardDuck.action(DASHBOARD_ACTIONS.REQUEST_SHARE_URL)
DashboardDuck.action(DASHBOARD_ACTIONS.STORE_SHARE_KEY)
DashboardDuck.action(DASHBOARD_ACTIONS.DELETE_SHARE_KEY)
DashboardDuck.action(DASHBOARD_ACTIONS.SEND_INVITATION)
DashboardDuck.action(DASHBOARD_ACTIONS.SEND_SCHEMA)
DashboardDuck.action(DASHBOARD_ACTIONS.SEND_SOURCE)
DashboardDuck.action(DASHBOARD_ACTIONS.CHANGE_SCHEMA)
DashboardDuck.action(DASHBOARD_ACTIONS.RESET_SCHEMA)
DashboardDuck.action(DASHBOARD_ACTIONS.SEND_REPORTS_ORDER)
DashboardDuck.action(DASHBOARD_ACTIONS.UPDATE_BACKEND_RESULT)
DashboardDuck.action(DASHBOARD_ACTIONS.UPDATE_DASHBOARD)

// selectors
DashboardDuck.selector('title', state => state.title || '')

DashboardDuck.selector('blocks', state => state.currentSchema?.get('blocks'))

DashboardDuck.selector('currentSchema', state => state.currentSchema || getEmptyDashboardSchemaModel())

DashboardDuck.selector('remoteSchema', state => state.remoteSchema || getEmptyDashboardSchemaModel())

DashboardDuck.selector('blocksListSchema', (state, duckFace) => {
  const blocks = duckFace.select.blocks(state)
  return (
    blocks
      ? [
          ...(blocks.valueSeq().map(block => {
            return { uid: block.get('uid'), type: block.get('type') }
          }) as unknown as Iterable<{ uid: string; type: string }>)
        ]
      : []
  ) as Array<{ uid: string; type: string }>
})

DashboardDuck.selector('activeTabID', state => state.currentSchema.get('activeTabID') || null)

DashboardDuck.selector(
  'userTimeZone',
  state =>
    state.currentSchema.get('timeZone') || {
      title: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offset: getDefaultTimezoneOffset()
    }
)

DashboardDuck.selector('showSidebar', state => state.currentSchema.get('showSidebar') || null)

DashboardDuck.selector('currentBlockModel', state => {
  const blocks = state.currentSchema?.get<BlocksOrderedMap>('blocks')
  const uid = state.currentSchema?.get('currentBlockUID')
  return (blocks ? blocks.getIn([uid]) || blocks.getIn([0]) : null) as BlockModel | null
})

DashboardDuck.selector('blockCount', state => state.currentSchema?.get<BlocksOrderedMap>('blocks')?.size)

DashboardDuck.selector('error', state => state.error)

DashboardDuck.selector('invitationError', state => state.invitationError)

DashboardDuck.selector('shareRequested', state => state.shareRequested)

DashboardDuck.selector('shareURLs', state => state.shareKeys ?? [])

DashboardDuck.selector('invitationStatus', state => state.invitationStatus || 0)

DashboardDuck.selector('schemaState', state => state.schemaState || null)

DashboardDuck.selector('shareKeyRequestError', state => Boolean(state.shareKeyRequestError))

// reducers
DashboardDuck.reducer<{ title: string }>(DASHBOARD_ACTIONS.UPDATE_TITLE, (state, action) => {
  const { title } = action.payload
  return {
    ...state,
    title
  }
})

DashboardDuck.reducer<{ title: string }>(DASHBOARD_ACTIONS.RECEIVE_TITLE, (state, action) => {
  const { title } = action.payload
  return {
    ...state,
    title
  }
})

DashboardDuck.reducer<Partial<{ error: string; initialActionType?: string }>>(
  DASHBOARD_ACTIONS.UPDATE_BACKEND_RESULT,
  (state, action) => {
    const { error, initialActionType } = action.payload

    if (initialActionType === DASHBOARD_ACTIONS.REQUEST_SHARE_URL) {
      return {
        ...state,
        shareKeyRequestError: Boolean(error)
      }
    }
    return {
      ...state,
      error
    }
  }
)

DashboardDuck.reducer<{
  error: string
  data: Partial<DashboardSchemaModel> | null
}>(DASHBOARD_ACTIONS.CHANGE_SCHEMA, (state, { payload }) => {
  const { error, data } = payload
  return data
    ? {
        ...state,
        currentSchema: mergeSource(state.currentSchema, data),
        remoteSchema: mergeSource(state.remoteSchema, data),
        schemaState: 'update'
      }
    : { ...state, error }
})

DashboardDuck.reducer<{
  error: string
  data: Partial<DashboardSchemaModel> | null
}>(DASHBOARD_ACTIONS.RESET_SCHEMA, (state, { payload }) => {
  const { error, data } = payload
  return data
    ? {
        ...state,
        currentSchema: getDashboardSchemaModelFrom(data),
        remoteSchema: getDashboardSchemaModelFrom(data),
        schemaState: 'update'
      }
    : { ...state, error }
})

DashboardDuck.reducer<string | null>(DASHBOARD_ACTIONS.REQUEST_SHARE_UI, (state, action) => {
  const shareRequested = action.payload
  return {
    ...state,
    shareRequested: shareRequested
  }
})

DashboardDuck.reducer<{ uid: string; url: string }>(DASHBOARD_ACTIONS.STORE_SHARE_KEY, (state, action) => {
  const { uid, url } = action.payload
  return state.shareKeys.some(({ uid: itemUID }) => itemUID === uid)
    ? state
    : {
        ...state,
        shareKeys: state.shareKeys.concat([{ uid, url }])
      }
})

DashboardDuck.reducer<{ sharedKey: string; email: string } | { sendStatus: SendStatusType; error?: string }>(
  DASHBOARD_ACTIONS.SEND_INVITATION,
  (state, action) => {
    if ('sendStatus' in action.payload) {
      const { sendStatus, error } = action.payload
      const nextError =
        'number' === typeof sendStatus
          ? SendStatus.SentWithError === sendStatus
            ? error
            : SendStatus.Send === sendStatus
            ? null
            : null
          : null

      return {
        ...state,
        invitationError: nextError,
        invitationStatus: 'number' === typeof sendStatus ? sendStatus : SendStatus.Sending
      }
    }
    return state
  }
)

DashboardDuck.reducer<Partial<InsightsDashboardPoolState>>(DASHBOARD_ACTIONS.UPDATE_DASHBOARD, (state, action) => {
  const update = action.payload || null
  return {
    ...state,
    ...update
  }
})

DashboardDuck.reducer<{ newPortal: object }>(DASHBOARD_ACTIONS.CLONE_SET, (state, action) =>
  updateCurrentData(state, action.payload)
)

function mergeSource(schema: DashboardSchemaModel, data: Partial<DashboardSchemaModel>): DashboardSchemaModel {
  return schema && data
    ? schema.merge({
        createdAt: data.createdAt,
        createdBy: data.createdBy,
        updatedAt: data.updatedAt,
        updatedBy: data.updatedBy,
        filters: data.filters
      })
    : schema
}
